import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Lock, User, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";
import { SecurityQuestionSetup } from "@/components/SecurityQuestionSetup";
import { PasswordResetForm } from "@/components/PasswordResetForm";
import { cleanupAuthState, signOutGlobally, reactivateUserIfNeeded, getCorrectDashboardRoute } from "@/lib/auth-utils";
import { BackToHome } from "@/components/BackToHome";
import { Checkbox } from "@/components/ui/checkbox";

const MentorAuth = () => {
  useEffect(() => {
    document.title = "Mentor Portal - Ascendry";
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [showSecuritySetup, setShowSecuritySetup] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [newUserData, setNewUserData] = useState<any>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Sign In Form State
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  // Sign Up Form State
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // REMOVED: Problematic useEffect that was causing redirect conflicts

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log('🔵 Starting sign in process');
      
      // Clean up any existing auth state first
      console.log('🔵 Cleaning up auth state');
      cleanupAuthState();
      
      // Small delay for Safari compatibility
      console.log('🔵 Adding delay for Safari compatibility');
      await new Promise(resolve => setTimeout(resolve, 100));


      console.log('🔵 Attempting Supabase sign in with email:', signInEmail);
      
      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('ascendry_remember_me', 'true');
      } else {
        localStorage.removeItem('ascendry_remember_me');
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: signInEmail,
        password: signInPassword,
      });

      console.log('🔵 Supabase sign in response:', { data, error });

      if (error) {
        console.error('🔴 Supabase sign in error:', error);
        throw error;
      }

      if (!data?.user) {
        console.error('🔴 No user returned from authentication');
        throw new Error('No user returned from authentication');
      }

      console.log('🔵 Sign in successful, user:', data.user.email);

      // For admin users (lance@blaqout.us), use direct redirect
      if (data.user.email === 'lance@blaqout.us') {
        toast({
          title: "Welcome back, Admin!",
          description: "You have been signed in successfully.",
        });

        // Force page reload to clear any Safari caching issues
        window.location.href = '/dashboard/admin';
        return;
      }
      
      // For other users, verify their mentor status with timeout protection
      try {
        // Try to load profile with timeout protection
        const profilePromise = supabase
          .from('profiles')
          .select('role, is_active')
          .eq('user_id', data.user.id)
          .maybeSingle();

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Profile fetch timeout')), 8000)
        );

        let profile: any = null;
        try {
          const { data: prof, error: profileError } = await Promise.race([
            profilePromise,
            timeoutPromise
          ]) as any;
          if (!profileError) profile = prof;
        } catch (e) {
          console.warn('Profile fetch timed out or failed, will try recovery path.');
        }

        // If profile missing, attempt to create a minimal one (allowed by RLS for own user)
        if (!profile) {
          try {
            const { data: created } = await supabase
              .from('profiles')
              .insert({
                user_id: data.user.id,
                email: data.user.email || '',
                role: 'mentor',
              })
              .select('role, is_active')
              .maybeSingle();
            if (created) profile = created;
          } catch (e) {
            console.warn('Profile create failed or already exists:', e);
          }
        }

        // If still no profile, verify mentor access via mentors table
        if (!profile) {
          // Fallback 1: mentor record linked by user_id
          const { data: mentorData } = await supabase
            .from('mentors')
            .select('is_approved, is_active')
            .eq('user_id', data.user.id)
            .maybeSingle();

          if (mentorData?.is_approved && mentorData?.is_active) {
            toast({
              title: 'Welcome back!',
              description: 'You have been signed in successfully.',
            });
            window.location.href = '/dashboard/mentor';
            return;
          }

          // Fallback 2: mentor record by email (visible via public policy when approved + active)
          const { data: mentorByEmail } = await supabase
            .from('mentors')
            .select('is_approved, is_active')
            .eq('email', data.user.email)
            .maybeSingle();

          if (mentorByEmail?.is_approved && mentorByEmail?.is_active) {
            toast({
              title: 'Welcome back!',
              description: 'You have been signed in successfully.',
            });
            window.location.href = '/dashboard/mentor';
            return;
          }

          throw new Error('Unable to verify your mentor status. Please contact support if you have an approved application.');
        }

        // Profile exists: route based on role
        if (profile?.role === 'mentor' || profile?.role === 'admin') {
          toast({
            title: 'Welcome back!',
            description: 'You have been signed in successfully.',
          });

          const dashboardRoute = profile?.role === 'admin' ? '/dashboard/admin' : '/dashboard/mentor';
          window.location.href = dashboardRoute;
          return;
        } else {
          toast({
            title: 'Access Denied',
            description: 'This login is for mentors and admins only.',
            variant: 'destructive',
          });
          await supabase.auth.signOut();
        }
      } catch (profileFetchError) {
        console.error('Profile fetch failed:', profileFetchError);
        toast({
          title: "Access Error",
          description: "Unable to verify your access level. Please contact support.",
          variant: "destructive",
        });
        await supabase.auth.signOut();
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      toast({
        title: "Sign In Failed",
        description: error.message || "Please check your credentials and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (signUpPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match.",
        variant: "destructive",
      });
      return;
    }

    if (signUpPassword.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Create mentor account without requiring application first
      const { data, error } = await supabase.auth.signUp({
        email: signUpEmail,
        password: signUpPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/mentor`,
          data: {
            first_name: firstName,
            last_name: lastName,
            role: 'mentor'
          }
        }
      });

      if (error) throw error;

      if (data.user) {
        // Store user data for security question setup
        setNewUserData(data.user);
        setShowSecuritySetup(true);
        
        toast({
          title: "Account Created!",
          description: "Please set up your security question to complete registration.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An error occurred during sign up.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecurityQuestionComplete = () => {
    setShowSecuritySetup(false);
    toast({
      title: "Registration Complete!",
      description: "You can now access your mentor dashboard.",
    });
    navigate('/dashboard/mentor');
  };

  const handleResetSuccess = () => {
    setShowResetForm(false);
    toast({
      title: "Password Reset Complete",
      description: "You can now sign in with your new password.",
    });
  };

  if (showSecuritySetup) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <SecurityQuestionSetup 
            userId={newUserData?.id}
            onComplete={handleSecurityQuestionComplete}
            title="Complete Your Registration"
          />
        </div>
      </div>
    );
  }

  if (showResetForm) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <Button 
            variant="ghost" 
            onClick={() => setShowResetForm(false)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </Button>
          <PasswordResetForm 
            onSuccess={handleResetSuccess}
            title="Reset Your Password"
            isForForgotPassword={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-mentor-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <img 
            src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
            alt="Ascendry" 
            className="h-12 w-auto mx-auto"
          />
          <h1 className="text-2xl font-bold text-mentor-glow">Mentor Portal</h1>
          <p className="text-muted-foreground">Expert mentors. Premium experience.</p>
        </div>

        <Card className="mentor-card">
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <CardHeader>
                <CardTitle className="text-mentor-glow">Welcome Back</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="mentor@example.com"
                        value={signInEmail}
                        onChange={(e) => setSignInEmail(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="signin-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="remember-me"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                    />
                    <Label 
                      htmlFor="remember-me"
                      className="text-sm font-normal cursor-pointer"
                    >
                      Remember me on this device
                    </Label>
                  </div>

                  <Button 
                    type="submit" 
                    variant="mentor"
                    className="w-full" 
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing In..." : "Sign In"}
                  </Button>

                  <div className="text-center">
                    <Button 
                      type="button"
                      variant="link" 
                      className="text-sm text-mentor-primary hover:text-mentor-accent"
                      onClick={() => setShowResetForm(true)}
                    >
                      Forgot your password?
                    </Button>
                  </div>
                </form>
              </CardContent>
            </TabsContent>

            <TabsContent value="signup">
              <CardHeader>
                <CardTitle className="text-mentor-glow">Create Mentor Account</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first-name">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input
                          id="first-name"
                          type="text"
                          placeholder="John"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="last-name">Last Name</Label>
                      <Input
                        id="last-name"
                        type="text"
                        placeholder="Doe"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="mentor@example.com"
                        value={signUpEmail}
                        onChange={(e) => setSignUpEmail(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="signup-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Choose a strong password"
                        value={signUpPassword}
                        onChange={(e) => setSignUpPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        minLength={6}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      <Input
                        id="confirm-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10"
                        required
                        minLength={6}
                      />
                    </div>
                   </div>

                   {/* Terms and Privacy Agreement */}
                   <div className="space-y-3 border-t pt-4">
                     <div className="flex items-start space-x-2">
                        <Checkbox 
                          id="mentor-terms-agreement" 
                          checked={acceptedTerms}
                          onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
                          required
                          className="border-mentor-border data-[state=checked]:bg-mentor-primary data-[state=checked]:text-mentor-primary-foreground"
                        />
                       <label htmlFor="mentor-terms-agreement" className="text-sm leading-5">
                         I agree to the{" "}
                         <a 
                           href="/terms" 
                           target="_blank" 
                           rel="noopener noreferrer"
                            className="text-mentor-primary hover:text-mentor-primary-glow hover:underline"
                         >
                           Terms of Use
                         </a>
                       </label>
                     </div>
                     
                     <div className="flex items-start space-x-2">
                        <Checkbox 
                          id="mentor-privacy-agreement" 
                          checked={acceptedPrivacy}
                          onCheckedChange={(checked) => setAcceptedPrivacy(checked as boolean)}
                          required
                          className="border-mentor-border data-[state=checked]:bg-mentor-primary data-[state=checked]:text-mentor-primary-foreground"
                        />
                       <label htmlFor="mentor-privacy-agreement" className="text-sm leading-5">
                         I acknowledge the{" "}
                         <a 
                           href="/privacy" 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="text-mentor-primary hover:text-mentor-primary-glow hover:underline"
                         >
                           Privacy Policy
                         </a>
                       </label>
                     </div>
                   </div>

                   <Button 
                     type="submit" 
                     variant="mentor"
                     className="w-full" 
                     disabled={isLoading || !acceptedTerms || !acceptedPrivacy}
                  >
                    {isLoading ? "Creating Account..." : "Create Account"}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">Are you a student?</p>
          <Button variant="mentor-outline" size="sm" asChild>
            <Link to="/auth/student">Student Portal</Link>
          </Button>
        </div>

        <div className="text-center">
          <BackToHome />
        </div>
      </div>
    </div>
  );
};

export default MentorAuth;